/**
 * Post Editor Page
 * Create and edit posts with J2K2 AI integration
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Select } from '../components/ui/Select';
import type { Post, SocialPlatform, Template } from '../types';
import { Icon } from '../components/icons';
import { useToast } from '../components/ui/ToastProvider';

export function PostEditor() {
  const { id } = useParams();
  const { selectedBrand } = useBrand();
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [platforms, setPlatforms] = useState<SocialPlatform[]>(['instagram']);
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [bestTimes, setBestTimes] = useState<Array<{ platform: string; time: number; reason: string; confidence: number }>>([]);
  const [bestTimesAnalysis, setBestTimesAnalysis] = useState('');
  const [isFetchingBestTimes, setIsFetchingBestTimes] = useState(false);
  const [mediaAttachments, setMediaAttachments] = useState<Array<{ id?: string; url: string; name: string }>>([]);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState<Array<{ id: string; url: string; name: string }>>([]);
  const [isLoadingMediaLibrary, setIsLoadingMediaLibrary] = useState(false);

  // J2K2 Generation options
  const [promptInput, setPromptInput] = useState('');
  const [tone, setTone] = useState<'professional' | 'casual' | 'playful' | 'inspiring'>(
    'professional'
  );
  const [length, setLength] = useState<'short' | 'medium' | 'long'>('medium');
  const [useBrandDNA, setUseBrandDNA] = useState(true);
  const [isSuggestingHashtags, setIsSuggestingHashtags] = useState(false);

  useEffect(() => {
    if (isEditing && id) {
      fetchPost(id);
    }
  }, [id]);

  useEffect(() => {
    if (selectedBrand) {
      fetchTemplates();
      fetchMediaLibrary();
    } else {
      setTemplates([]);
      setMediaLibrary([]);
    }
  }, [selectedBrand]);

  const fetchPost = async (postId: string) => {
    try {
      const response = await apiClient.getPost(postId);
      if (response.success && response.data) {
        const post = response.data as Post;
        setTitle(post.title || '');
        setContent(post.content);
        setPlatforms(post.platforms);
        setHashtags(post.hashtags);
        setMediaAttachments(
          (post.media_urls || []).map((url) => ({
            url,
            name: url.split('/').pop() || 'Media',
          }))
        );

        // Load scheduled date/time if set
        if (post.scheduled_at) {
          const scheduledDate = new Date(post.scheduled_at);
          setScheduledDate(scheduledDate.toISOString().split('T')[0]);
          setScheduledTime(scheduledDate.toTimeString().slice(0, 5));
        }
      }
    } catch (error) {
      setError('Failed to load post');
    }
  };

  const fetchTemplates = async () => {
    if (!selectedBrand) return;
    setIsLoadingTemplates(true);
    try {
      const response = await apiClient.getTemplates(selectedBrand.id);
      if (response.success && response.data) {
        setTemplates(response.data as Template[]);
      }
    } catch (error) {
      console.error('Failed to fetch templates', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedBrand) {
      setError('Please select a brand before saving a template');
      return;
    }
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    setIsSavingTemplate(true);
    setError('');
    try {
      const response = await apiClient.createTemplate({
        brand_id: selectedBrand.id,
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        content_template: (templateContent || content).trim(),
        platforms,
        post_type: 'single',
        hashtags,
        is_public: false,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to save template');
      }

      setTemplateName('');
      setTemplateDescription('');
      setTemplateContent('');
      setSuccess('Template saved');
      showToast('Template saved', 'success');
      await fetchTemplates();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
      showToast(err instanceof Error ? err.message : 'Failed to save template', 'error');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleApplyTemplate = (template: Template) => {
    setTitle(template.name);
    setContent(template.content_template);
    if (template.platforms && template.platforms.length > 0) {
      setPlatforms(template.platforms);
    }
    if (template.hashtags && template.hashtags.length > 0) {
      setHashtags(template.hashtags);
    }
    setSuccess('Template applied');
    showToast('Template applied', 'success');
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      const response = await apiClient.deleteTemplate(templateId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete template');
      }
      await fetchTemplates();
      setSuccess('Template deleted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template');
      showToast(err instanceof Error ? err.message : 'Failed to delete template', 'error');
    }
  };

  const handleFetchBestTimes = async () => {
    if (!selectedBrand) {
      setError('Please select a brand to fetch best times');
      return;
    }
    setIsFetchingBestTimes(true);
    setError('');
    try {
      const response = await apiClient.getBestPostingTimes({
        brand_id: selectedBrand.id,
        platforms,
      });

      if (response.success && response.data) {
        setBestTimes(response.data.suggestions || []);
        setBestTimesAnalysis(response.data.analysis || '');
        if ((response.data.suggestions || []).length === 0) {
          setSuccess('No suggestions returned for the selected platforms');
          showToast('No best-time suggestions returned', 'info');
        }
      } else {
        setError(response.error || 'Failed to fetch best times');
        showToast(response.error || 'Failed to fetch best times', 'error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch best times');
      showToast(err instanceof Error ? err.message : 'Failed to fetch best times', 'error');
    } finally {
      setIsFetchingBestTimes(false);
    }
  };

  const applySuggestedTime = (time: number) => {
    const date = new Date(time);
    setScheduledDate(date.toISOString().split('T')[0]);
    setScheduledTime(date.toTimeString().slice(0, 5));
    setSuccess('Applied suggested time');
  };

  const handleGenerateCaption = async () => {
    if (!selectedBrand || !promptInput.trim()) {
      setError('Please select a brand and enter a prompt');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const response = await apiClient.generateCaption({
        brand_id: selectedBrand.id,
        prompt: promptInput,
        platform: platforms[0] || 'instagram',
        tone,
        length,
        include_hashtags: true,
        include_emoji: true,
        use_brand_dna: useBrandDNA,
      });

      if (response.success && response.data) {
        setContent(response.data.caption);
        if (response.data.hashtags && response.data.hashtags.length > 0) {
          setHashtags((prev) => [...new Set([...prev, ...(response.data?.hashtags || [])])]);
        }
        setSuccess('Caption generated successfully!');
        showToast('Caption generated successfully', 'success');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.error || 'Failed to generate caption');
        showToast(response.error || 'Failed to generate caption', 'error');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to generate caption');
      showToast(error instanceof Error ? error.message : 'Failed to generate caption', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUploadMedia = async (file: File) => {
    if (!selectedBrand) {
      setError('Please select a brand before uploading media');
      return;
    }

    setIsUploadingMedia(true);
    setError('');
    try {
      const response = await apiClient.uploadMedia(file, selectedBrand.id);
      if (response.success && response.data) {
        setMediaAttachments((prev) => [
          ...prev,
          { id: response.data!.id, url: response.data!.url, name: response.data!.filename },
        ]);
        setSuccess('Media uploaded');
        showToast('Media uploaded', 'success');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setError(response.error || 'Failed to upload media');
        showToast(response.error || 'Failed to upload media', 'error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload media');
      showToast(err instanceof Error ? err.message : 'Failed to upload media', 'error');
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const handleRemoveMedia = async (attachment: { id?: string; url: string }) => {
    setMediaAttachments((prev) => prev.filter((item) => item.url !== attachment.url));
    if (attachment.id) {
      try {
        await apiClient.deleteMedia(attachment.id);
      } catch (err) {
        console.error('Failed to delete media from server', err);
      }
    }
  };

  const handleSuggestHashtags = async () => {
    if (!selectedBrand) {
      const msg = 'Please select a brand';
      setError(msg);
      showToast(msg, 'error');
      return;
    }
    if (!content.trim()) {
      const msg = 'Enter content before suggesting hashtags';
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    setIsSuggestingHashtags(true);
    try {
      const response = await apiClient.generateHashtags({
        brand_id: selectedBrand.id,
        content,
        platform: platforms[0] || 'instagram',
        count: 8,
      });
      if (response.success && response.data?.hashtags) {
        const newTags = response.data.hashtags.filter((tag) => !hashtags.includes(tag));
        setHashtags((prev) => [...prev, ...newTags]);
        showToast('Hashtags suggested', 'success');
      } else {
        showToast(response.error || 'Failed to suggest hashtags', 'error');
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to suggest hashtags', 'error');
    } finally {
      setIsSuggestingHashtags(false);
    }
  };

  const fetchMediaLibrary = async () => {
    if (!selectedBrand) return;
    setIsLoadingMediaLibrary(true);
    try {
      const response = await apiClient.listMedia({ brand_id: selectedBrand.id, limit: 50 });
      if (response.success && response.data) {
        setMediaLibrary(
          response.data.map((item) => ({
            id: item.id,
            url: (item as any).url || (item as any).r2_url || item.filename,
            name: item.filename,
          }))
        );
      }
    } catch (err) {
      console.error('Failed to load media library', err);
    } finally {
      setIsLoadingMediaLibrary(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedBrand || !content.trim()) {
      const msg = 'Please select a brand and enter content';
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      if (isEditing && id) {
        await apiClient.updatePost(id, {
          title: title.trim() || undefined,
          content: content.trim(),
          media_urls: mediaAttachments.map((m) => m.url),
          platforms,
          hashtags,
        });
        setSuccess('Post updated successfully!');
        showToast('Post updated successfully', 'success');
      } else {
        const response = await apiClient.createPost({
          brand_id: selectedBrand.id,
          title: title.trim() || undefined,
          content: content.trim(),
          media_urls: mediaAttachments.map((m) => m.url),
          platforms,
          hashtags,
        });

        if (response.success && response.data?.id) {
          setSuccess('Post saved as draft!');
          showToast('Draft saved', 'success');
          setTimeout(() => {
            navigate(`/posts/${response.data?.id}`);
          }, 1500);
        }
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to save post');
      showToast(error instanceof Error ? error.message : 'Failed to save post', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSchedule = async () => {
    if (!selectedBrand || !content.trim()) {
      const msg = 'Please select a brand and enter content';
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    if (!scheduledDate || !scheduledTime) {
      setError('Please select a date and time for scheduling');
      showToast('Please select a date and time for scheduling', 'error');
      return;
    }

    // Combine date and time into timestamp
    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
    const scheduledTimestamp = scheduledDateTime.getTime();

    // Validate future date
    if (scheduledTimestamp <= Date.now()) {
      setError('Scheduled time must be in the future');
      return;
    }

    setIsScheduling(true);
    setError('');

    try {
      let postId = id;

      // Create or update post first
      if (!isEditing) {
        const response = await apiClient.createPost({
          brand_id: selectedBrand.id,
          title: title.trim() || undefined,
          content: content.trim(),
          media_urls: mediaAttachments.map((m) => m.url),
          platforms,
          hashtags,
        });

        if (!response.success || !response.data?.id) {
          throw new Error('Failed to create post');
        }
        postId = response.data.id;
      }

      // Schedule the post
      if (postId) {
        await apiClient.updatePost(postId, {
          media_urls: mediaAttachments.map((m) => m.url),
          scheduled_at: scheduledTimestamp,
        });

        setSuccess(`Post scheduled for ${scheduledDateTime.toLocaleString()}!`);
        showToast('Post scheduled', 'success');
        setTimeout(() => {
          navigate('/calendar');
        }, 1500);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to schedule post');
      showToast(error instanceof Error ? error.message : 'Failed to schedule post', 'error');
    } finally {
      setIsScheduling(false);
    }
  };

  const handlePublishNow = async () => {
    if (!selectedBrand || !content.trim()) {
      const msg = 'Please select a brand and enter content';
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    setIsPublishing(true);
    setError('');

    try {
      let postId = id;

      // Create post if new
      if (!isEditing) {
        const response = await apiClient.createPost({
          brand_id: selectedBrand.id,
          title: title.trim() || undefined,
          content: content.trim(),
          platforms,
          hashtags,
        });

        if (!response.success || !response.data?.id) {
          throw new Error('Failed to create post');
        }
        postId = response.data.id;
      }

      // Publish immediately by setting scheduled_at to now
      if (postId) {
        await apiClient.updatePost(postId, {
          media_urls: mediaAttachments.map((m) => m.url),
          scheduled_at: Date.now(),
        });

        setSuccess('Post published successfully!');
        showToast('Post published', 'success');
        setTimeout(() => {
          navigate('/posts');
        }, 1500);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to publish post');
      showToast(error instanceof Error ? error.message : 'Failed to publish post', 'error');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleAddHashtag = () => {
    const tag = hashtagInput.trim().replace('#', '');
    if (tag && !hashtags.includes(tag)) {
      setHashtags([...hashtags, tag]);
      setHashtagInput('');
    }
  };

  const handleRemoveHashtag = (tag: string) => {
    setHashtags(hashtags.filter((t) => t !== tag));
  };

  const togglePlatform = (platform: SocialPlatform) => {
    if (platforms.includes(platform)) {
      setPlatforms(platforms.filter((p) => p !== platform));
    } else {
      setPlatforms([...platforms, platform]);
    }
  };

  const platformOptions: SocialPlatform[] = [
    'instagram',
    'facebook',
    'twitter',
    'linkedin',
    'tiktok',
    'youtube',
  ];

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center">
          <p className="text-gray-500">Please select a brand from the dashboard</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (user?.role === 'viewer') {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center">
          <p className="text-gray-500">You have view-only access. Ask an admin to upgrade your role to create or edit posts.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate('/posts')}
          >
            Back to Posts
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {isEditing ? 'Edit Post' : 'Create New Post'}
        </h1>
        <p className="text-gray-600 mt-1 text-sm sm:text-base">
          Creating content for{' '}
          <span
            className="font-medium"
            style={{ color: selectedBrand.color_primary }}
          >
            {selectedBrand.name}
          </span>
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* J2K2 AI Generator */}
      <Card>
        <CardHeader
          title="AI Caption Generator"
          subtitle="Generate brand-consistent content with J2K2"
        />

        <div className="space-y-4">
          <Textarea
            label="Describe your post"
            placeholder="E.g., Post about ocean sounds for relaxation and focus"
            value={promptInput}
            onChange={(e) => setPromptInput(e.target.value)}
            rows={3}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Tone"
              options={[
                { value: 'professional', label: 'Professional' },
                { value: 'casual', label: 'Casual' },
                { value: 'playful', label: 'Playful' },
                { value: 'inspiring', label: 'Inspiring' },
              ]}
              value={tone}
              onChange={(e) =>
                setTone(e.target.value as typeof tone)
              }
            />

            <Select
              label="Length"
              options={[
                { value: 'short', label: 'Short' },
                { value: 'medium', label: 'Medium' },
                { value: 'long', label: 'Long' },
              ]}
              value={length}
              onChange={(e) =>
                setLength(e.target.value as typeof length)
              }
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="useBrandDNA"
              type="checkbox"
              checked={useBrandDNA}
              onChange={(e) => setUseBrandDNA(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="useBrandDNA" className="text-sm text-gray-700">
              Use Brand DNA context (tone, phrases, hashtags) in generation
            </label>
          </div>

          <Button
            onClick={handleGenerateCaption}
            isLoading={isGenerating}
            variant="secondary"
            className="w-full"
          >
            <Icon name="sparkles" size="sm" className="mr-2" />
            Generate with J2K2
          </Button>
        </div>
      </Card>

      {/* Templates */}
      <Card>
        <CardHeader
          title="Templates"
          subtitle="Save and reuse post structures for this brand"
        />

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Input
                label="Template name"
                placeholder="e.g., Product launch teaser"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
              <Textarea
                label="Template content"
                placeholder="Optional preset content; defaults to current caption"
                value={templateContent}
                onChange={(e) => setTemplateContent(e.target.value)}
                rows={4}
              />
              <Textarea
                label="Description (optional)"
                placeholder="Add context for teammates"
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                rows={2}
              />
              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setTemplateContent(content)}
                >
                  Use current caption
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveTemplate}
                  isLoading={isSavingTemplate}
                >
                  <Icon name="save" size="sm" className="mr-2" />
                  Save Template
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Saved templates</h3>
                {isLoadingTemplates && (
                  <span className="text-xs text-gray-500">Loading…</span>
                )}
              </div>
              {templates.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No templates yet. Save your first one to speed up recurring posts.
                </p>
              ) : (
                <div className="space-y-3">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="border border-gray-200 rounded-lg p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="font-medium text-gray-900">{template.name}</p>
                          {template.description && (
                            <p className="text-sm text-gray-500 line-clamp-2">
                              {template.description}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => handleApplyTemplate(template)}
                          >
                            Use
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                        {template.content_template}
                      </p>
                      {template.platforms?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {template.platforms.map((platform) => (
                            <span
                              key={platform}
                              className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 capitalize"
                            >
                              {platform}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Media Upload */}
      <Card>
        <CardHeader title="Media Attachments" subtitle="Upload images or videos to include with this post" />
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              type="file"
              accept="image/*,video/mp4"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadMedia(file);
              }}
              className="block w-full text-sm text-gray-600"
            />
            {isUploadingMedia && (
              <span className="text-xs text-gray-500">Uploading...</span>
            )}
          </div>

          {mediaAttachments.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {mediaAttachments.map((media) => (
                <div
                  key={media.url}
                  className="border border-gray-200 rounded-lg p-3 flex items-center gap-3"
                >
                  <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                    <img
                      src={media.url}
                      alt={media.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <Icon name="image" size="md" className="text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{media.name}</p>
                    <p className="text-xs text-gray-500 truncate">{media.url}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveMedia(media)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-800">Media Library</p>
              {isLoadingMediaLibrary && (
                <span className="text-xs text-gray-500">Refreshing…</span>
              )}
              <Button size="sm" variant="outline" onClick={fetchMediaLibrary}>
                Refresh
              </Button>
            </div>
            {mediaLibrary.length === 0 ? (
              <p className="text-sm text-gray-500">No media yet. Upload to populate your library.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {mediaLibrary.map((media) => {
                  const isSelected = mediaAttachments.some((m) => m.url === media.url);
                  return (
                    <button
                      key={media.id}
                      type="button"
                      onClick={() => {
                        if (!isSelected) {
                          setMediaAttachments((prev) => [...prev, { id: media.id, url: media.url, name: media.name }]);
                          showToast('Media attached', 'success');
                        }
                      }}
                      className={`border rounded-lg p-2 flex flex-col items-start gap-2 hover:border-blue-400 transition-colors ${
                        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="w-full h-24 bg-gray-100 rounded overflow-hidden flex items-center justify-center">
                        <img
                          src={media.url}
                          alt={media.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <Icon name="image" size="md" className="text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-700 truncate w-full text-left">{media.name}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Post Content */}
      <Card>
        <CardHeader title="Post Content" />

        <div className="space-y-4">
          <Input
            label="Title (optional)"
            placeholder="Give your post a title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <Textarea
            label="Caption"
            placeholder="Write or generate your post caption..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={8}
            required
          />

          {/* Platforms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platforms
            </label>
            <div className="flex flex-wrap gap-2">
              {platformOptions.map((platform) => (
                <button
                  key={platform}
                  type="button"
                  onClick={() => togglePlatform(platform)}
                  className={`px-4 py-2 rounded-lg border-2 transition-colors capitalize ${
                    platforms.includes(platform)
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {/* Hashtags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hashtags
            </label>
            <div className="flex flex-col sm:flex-row gap-2 mb-2">
              <Input
                placeholder="Enter hashtag"
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddHashtag();
                  }
                }}
              />
              <div className="flex gap-2">
                <Button type="button" onClick={handleAddHashtag} variant="outline">
                  Add
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleSuggestHashtags}
                  isLoading={isSuggestingHashtags}
                >
                  <Icon name="hash" size="sm" className="mr-2" />
                  Suggest
                </Button>
              </div>
            </div>
            {hashtags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {hashtags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    #{tag}
                    <button
                      onClick={() => handleRemoveHashtag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Scheduling */}
      <Card>
        <CardHeader title="Schedule Post" subtitle="Set a date and time to publish automatically" />

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Date"
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
            <Input
              label="Time"
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-gray-600">
                Not sure when to post? Let J2K2 suggest the best times for your selected platforms.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleFetchBestTimes}
                isLoading={isFetchingBestTimes}
              >
                <Icon name="clock" size="sm" className="mr-2" />
                Suggest best times
              </Button>
            </div>

            {bestTimes.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bestTimes.map((item) => (
                  <div
                    key={`${item.platform}-${item.time}`}
                    className="border border-gray-200 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold capitalize">{item.platform}</span>
                      <span className="text-xs text-gray-500">
                        {Math.round(item.confidence * 100)}% match
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 mt-1">
                      {new Date(item.time).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.reason}</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="mt-2 w-full"
                      onClick={() => applySuggestedTime(item.time)}
                    >
                      Use this time
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {bestTimesAnalysis && (
              <div className="bg-blue-50 border border-blue-100 text-blue-800 text-sm rounded-lg p-3">
                {bestTimesAnalysis}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <Button variant="outline" onClick={() => navigate('/posts')} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button onClick={handleSaveDraft} isLoading={isSaving} variant="secondary" className="w-full sm:w-auto">
          <Icon name="save" size="sm" className="mr-2" />
          {isEditing ? 'Update' : 'Save Draft'}
        </Button>
        <Button
          onClick={handleSchedule}
          isLoading={isScheduling}
          disabled={!scheduledDate || !scheduledTime}
          className="w-full sm:w-auto"
        >
          <Icon name="calendar" size="sm" className="mr-2" />
          Schedule Post
        </Button>
        <Button onClick={handlePublishNow} isLoading={isPublishing} variant="primary" className="w-full sm:w-auto">
          <Icon name="send" size="sm" className="mr-2" />
          Publish Now
        </Button>
      </div>
    </div>
  );
}
